import { ChallengeLearningType } from './Challenge';

export enum JamTaskStatus {
  READY_TO_START = 'READY_TO_START',
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE',
}

export enum JamEventStatusForSidebar {
  LOCKED = 'LOCKED',
  SOLVED = 'SOLVED',
}

export enum SolveChallengeButtonKey {
  AWS_CLI = 'aws-cli',
  AWS_CONSOLE = 'aws-console',
  CODE_WHISPERER = 'amazon-Q',
}

export interface JamTaskProgress {
  taskId: string;
  taskNumber: number;
  locked: boolean;
  started: boolean;
  completed: boolean;
  pointsPossible: number;
  pointsEarned: number;
  score: number;
  cluePenalties: [];
  usedClues: [];
}
export class JamProgress {
  eventName: string;
  challengeId: string;
  pointsPossible: number;
  pointsEarned: number;
  taskProgress: JamTaskProgress[];
  score: number;
  started: boolean;
  completed: boolean;
  remainingRestarts: number;
  hasAwsResources: boolean;
  hasExternalResources: boolean;
  // True when a lab-backed challenge has been started but the team's lab account
  // has not been assigned yet. While true, every task is returned locked with
  // content/clues stripped, and solving actions are rejected by the backend.
  awaitingLabAssignment: boolean;

  static fromPlainObject(obj: any): JamProgress {
    const data = Object.assign(new JamProgress(), obj) as JamProgress;
    return data;
  }
}

export class JamTaskClue {
  order: number;
  title: string;
  description?: string;
  cluePenalty: number;
  used: boolean;

  static fromPlainObject(obj: any): JamTaskClue {
    const data = Object.assign(new JamTaskClue(), obj) as JamTaskClue;
    return data;
  }
}

export interface JamCastleDefenseDetails {
  provider: string;
}

export interface JamArenaRequirement {
  id: string;
  statement: string;
  criticality: 'HARD' | 'STANDARD';
  dimensionKey: string;
}

export interface JamArenaRubric {
  below: string;
  acceptable: string;
  excellent: string;
}

export interface JamArenaDimension {
  key: string;
  name: string;
  weight: number;
  /** The quality bar is learner-visible by design. */
  rubric: JamArenaRubric;
}

export interface JamArenaWriteUp {
  architecture?: string;
  decisions?: string;
  requirementsCoverage?: string;
}

/** Learner-visible projection of the Arena definition (the answer key is never sent). */
export interface JamArenaProjection {
  scenarioBrief: string;
  objective: string;
  requirements: JamArenaRequirement[];
  dimensions: JamArenaDimension[];
  scoringReference?: { maxPoints: number };
}

/** The calling team's Arena state for this challenge, as of loadChallenge. */
export interface JamArenaTeamState {
  writeUp?: JamArenaWriteUp;
  version?: number;
  lastEditedBy?: string;
  lastEditedAt?: number;
  submissionCount: number;
  verdictStatus?: 'PENDING' | 'SCORED' | 'ERROR';
  bankedPoints: number;
}

export interface JamArenaDefinition {
  projection: JamArenaProjection;
  team: JamArenaTeamState;
}

export class JamArenaWriteUpState {
  writeUp: JamArenaWriteUp;
  /** Optimistic-lock version the client must echo on the next save. */
  version: number;
  lastEditedBy?: string;
  lastEditedAt?: number;

  static fromPlainObject(obj: any): JamArenaWriteUpState {
    return Object.assign(new JamArenaWriteUpState(), obj) as JamArenaWriteUpState;
  }
}

export class JamArenaSubmitResult {
  submissionNumber: number;
  submissionsRemaining: number;

  static fromPlainObject(obj: any): JamArenaSubmitResult {
    return Object.assign(new JamArenaSubmitResult(), obj) as JamArenaSubmitResult;
  }
}

export enum JamArenaVerdictStatus {
  PENDING = 'PENDING',
  SCORED = 'SCORED',
  ERROR = 'ERROR',
}

export interface JamArenaDimensionVerdict {
  key: string;
  score: number;
  reasoning: string;
}

export class JamArenaVerdict {
  status: JamArenaVerdictStatus;
  submissionNumber: number;
  submittedAt?: number;
  dimensions?: JamArenaDimensionVerdict[];
  totalFraction?: number;
  grossPoints?: number;
  evidenceUnavailable?: boolean;
  error?: string;

  static fromPlainObject(obj: any): JamArenaVerdict {
    return Object.assign(new JamArenaVerdict(), obj) as JamArenaVerdict;
  }
}

export interface JamLearningOutcome {
  summary: string;
  content: string;
  introduction: string;
  topicsCovered: string;
  technicalKnowledgePrerequisites: string;
  challengeDifficulty: string;
}

export interface JamTask {
  id: string;
  taskNumber: number;
  scorePercentage: number;
  title: string;
  background?: string;
  content: string;
  clues: JamTaskClue[];
  allowInputAnswer: boolean;
  validatedByLambda: boolean;
  pointsPossible: number;
  pointsEarned: number;
  score: number;
  status: JamTaskStatus;
  locked: boolean;
  cluesPenalty: number;
  inventory?: string;
}

export class JamChallengeDetails {
  title: string;
  category: string;
  description: string;
  learningType: ChallengeLearningType;
  difficulty: number;
  possiblePoints: number;
  pointsEarned: number;
  score: number;
  mode: string;
  challengeIcon: string;
  awsServices: string[];
  sshKeyPairRequired: boolean;
  castleDefenseDetails: JamCastleDefenseDetails;
  tasks: JamTask[];
  learningOutcome: JamLearningOutcome;
  status: JamTaskStatus;
  awsLabAccount: string;
  accountRegion: string;
  labExpirationTime: string;
  id: string;
  locked: boolean;
  // True when the challenge has been started but the team's lab account has not
  // been assigned yet. While true, every task is returned locked with
  // content/clues stripped, and solving actions are rejected by the backend.
  awaitingLabAssignment: boolean;
  /** True when the team's last lab timed out; the backend won't auto-assign another until a restart. */
  restartRequired?: boolean;
  /** Whether an unassigned lab exists for this challenge. Undefined means unknown, so don't gate on it. */
  labsAvailable?: boolean;
  /** Present only for SCENARIO challenges, which are judged via Arena, not tasks. */
  arena?: JamArenaDefinition;

  static fromPlainObject(obj: any): JamChallengeDetails {
    const data = Object.assign(new JamChallengeDetails(), obj) as JamChallengeDetails;
    return data;
  }
}

/**
 * Five-state lab status for a challenge, surfaced as a status icon in the sidebar.
 */
export enum ChallengeLabStatus {
  NOT_STARTED = 'NOT_STARTED',
  PREPARING = 'PREPARING',
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  SOLVED = 'SOLVED',
}

/**
 * Progress fields used to derive a {@link ChallengeLabStatus}. Accepts whichever
 * fields the caller has available from either {@link JamProgress} (selected
 * challenge, live) or `IJamChallenge` (list data).
 */
export interface ChallengeLabStatusInput {
  started?: boolean;
  /** From `JamProgress.completed`. */
  completed?: boolean;
  /** From `IJamChallenge.solved`. */
  solved?: boolean;
  hasAwsResources?: boolean;
  hasExternalResources?: boolean;
  awaitingLabAssignment?: boolean;
  /** Derived from `taskProgress?.some(t => t.completed)`. */
  anyTaskCompleted?: boolean;
}

/**
 * Pure mapping from challenge progress to a {@link ChallengeLabStatus}.
 *
 * Precedence: SOLVED > NOT_STARTED > PREPARING > IN_PROGRESS > READY.
 */
export const deriveChallengeLabStatus = (i: ChallengeLabStatusInput): ChallengeLabStatus => {
  if (i.completed || i.solved) {
    return ChallengeLabStatus.SOLVED;
  }
  if (!i.started) {
    return ChallengeLabStatus.NOT_STARTED;
  }
  const labBacked = !!(i.hasAwsResources || i.hasExternalResources);
  if (labBacked && i.awaitingLabAssignment) {
    return ChallengeLabStatus.PREPARING;
  }
  if (i.anyTaskCompleted) {
    return ChallengeLabStatus.IN_PROGRESS;
  }
  return ChallengeLabStatus.READY;
};
